import { action, makeObservable, observable } from 'mobx';

// types
import { IResult } from 'services/Result';
import { Invite, InviteUserResponse } from 'types/models/Invite';
import { IOrganizationsStore } from './OrganizationsStore';
import { INotificationsStore } from './NotificationsStore';

import { BaseStoreClass, IBaseStoreClass } from './BaseStoreClass';
import { IInviteService } from 'services/InviteService';
import { getErrorMessage, storeResponseHandler } from 'utilities/ErrorHelper';
import { t } from 'i18next';

export interface ISendInvite {
  organizationId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  type: string;
}

interface IInviteStore extends IBaseStoreClass {
  invites: Invite[];
  isLoaded: boolean;
  sendInvitation({ invitation }: { invitation: ISendInvite; }, CB?: (response: IResult<InviteUserResponse>) => void): Promise<boolean>;
}

export class InviteStore extends BaseStoreClass implements IInviteStore {
  invites: Invite[] = [];

  isLoaded: boolean = false;

  _notificationsStore: INotificationsStore;
  _organizationsStore: IOrganizationsStore;
  _inviteService: IInviteService;

  // Loading indicators
  isSendingInvite: boolean = false;

  constructor(inviteService: IInviteService, notificationsStore: INotificationsStore, organizationsStore: IOrganizationsStore) {
    super();
    this._notificationsStore = notificationsStore;
    this._organizationsStore = organizationsStore;
    this._inviteService = inviteService;

    makeObservable(this, {
      invites: observable,
      isLoaded: observable,
      isSendingInvite: observable,
      sendInvitation: action,
    });
  }

  public async sendInvitation({ invitation }: { invitation: ISendInvite }, CB?: (response: IResult<InviteUserResponse>) => void) {
    try {
      this.setIsSendingInvite(true);

      const response = await this._inviteService.sendInvite(invitation);
      storeResponseHandler(response, t('InvitationSentSuccessfully'), this._notificationsStore.addToast);

      if (response) {
        if (CB) CB(response);
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsSendingInvite(false);
    }

    return false;
  }

  // =========================== UTILS ===========================
  setIsSendingInvite(status: boolean) {
    this.isSendingInvite = status;
  }
}
